<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { deepCopyObject } from '@/appdata/globals';
import type { AppliedConfiguration, SwadeModConfigurationInterface, SwadeModificationInterface } from '@/types/items';
import { matchesConfiguration, resolvedModificationUuid } from '@/appdata/applied-configuration';
import { useUxData } from '@/stores/ux-data';

const props = defineProps<{
  source: { uuid: string, name: string },
  choiceSets: SwadeModificationInterface[],
  existingChoices?: AppliedConfiguration[],
}>();

const emit = defineEmits<{
  confirmed: [applied: AppliedConfiguration[]],
  cancelled: [],
}>();

const ux = useUxData();

// selected[choiceSetIndex] = the chosen option indexes. A single-mode set holds at most one.
const selected = ref<number[][]>([]);

const isMultiple = (cs: SwadeModificationInterface): boolean => cs.multiple === true;

const reset = () => {
  selected.value = props.choiceSets.map((cs) => {
    const existing = (props.existingChoices ?? []).filter(e => resolvedModificationUuid(e) === cs.uuid);
    if (existing.length === 0) return [];
    const options = (cs.configuration.element ?? []) as Partial<SwadeModificationInterface>[];
    const indexes = options
      .map((o, i) => (existing.some(e => matchesConfiguration(o.configuration, e.configuration)) ? i : -1))
      .filter(i => i >= 0);
    return isMultiple(cs) ? indexes : indexes.slice(0, 1);
  });
};
reset();
watch(() => [props.choiceSets, props.existingChoices], reset, { deep: true });

const allSelected = computed(() => selected.value.every(list => list.length >= 1));

// The `?? []` fallback below, and the two in confirm() (`selected.value[csIndex] ?? []` and
// `cs.configuration.element ?? []`), are structurally unreachable: reset() is the sole writer
// of `selected` and always gives it one array entry per choice set, populated only with
// indexes that are valid positions in that choice set's own `element` array. Verified
// unreachable by mutation (deleting each left the suite green) — don't backfill with a
// placebo test, and don't delete them blind.
const isSelected = (csIndex: number, optIndex: number) => (selected.value[csIndex] ?? []).includes(optIndex);

const select = (csIndex: number, optIndex: number) => {
  const next = selected.value.map(list => [...list]);
  const cs = props.choiceSets[csIndex];
  if (isMultiple(cs)) {
    const i = next[csIndex].indexOf(optIndex);
    if (i >= 0) {
      next[csIndex].splice(i, 1);
    } else {
      // A set may cap its picks (configuration.maxPicks — a repeated rung offers as many choices as
      // points invested, ruling 2026-09-24); a selection over the cap is ignored, never a replacement.
      const limit = typeof cs.configuration?.maxPicks === 'number' ? cs.configuration.maxPicks : Infinity;
      if (next[csIndex].length >= limit) return;
      next[csIndex].push(optIndex);
    }
    next[csIndex].sort((a, b) => a - b);
  } else {
    next[csIndex] = [optIndex];
  }
  selected.value = next;
};

const confirm = () => {
  const applied: AppliedConfiguration[] = props.choiceSets.flatMap((cs, csIndex) => {
    const options = (cs.configuration.element ?? []) as Partial<SwadeModificationInterface>[];
    return (selected.value[csIndex] ?? []).map((optIndex) => ({
      uuid: props.source.uuid,
      sourceModUuid: cs.uuid!,
      choiceSetUuid: cs.uuid!,
      configuration: deepCopyObject(options[optIndex].configuration) as SwadeModConfigurationInterface,
    }));
  });
  emit('confirmed', applied);
};

const cancel = () => emit('cancelled');

const optionLabel = (option: Partial<SwadeModificationInterface>) =>
  option.configuration?.label ?? '';
</script>

<template>
  <div class="choice-applicator">
    <div class="choice-applicator-body">
      <div v-for="(cs, csIndex) in choiceSets" :key="cs.uuid" class="choice-set mb-3">
        <div class="prompt fw-semibold mb-2">{{ cs.configuration.label }}</div>
        <div class="d-flex flex-fill flex-wrap justify-content-start gap-2" style="grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));">
          <button
            v-for="(option, optIndex) in (cs.configuration.element as any[])"
            :key="optIndex"
            type="button"
            data-choice-tile
            :class="['btn', 'flex-grow-1', 'btn-outline-primary', 'text-start', { selected: isSelected(csIndex, optIndex) }]"
            @click="select(csIndex, optIndex)"
          >
            <div class="fw-semibold">{{ optionLabel(option) }}</div>
            <!-- HTML, not text: a universal modifier's summary is the CMS entry's rich-text description.
                 Summaries authored in the app are stripped to plain text on input (the configurators)
                 and on an owned world's save (the API), so this sink only ever sees CMS markup. -->
            <small v-html="option?.configuration?.summary ?? ''"></small>
          </button>
        </div>
      </div>
    </div>
    <div class="choice-applicator-footer text-end mt-3">
      <button type="button" class="btn btn-secondary me-2" data-action="cancel" @click="cancel">
        {{ ux.labels?.actions?.cancel ?? 'Cancel' }}
      </button>
      <button type="button" class="btn btn-primary" data-action="confirm" :disabled="!allSelected" @click="confirm">
        {{ ux.labels?.swade?.data?.buildModifications?.choiceSet?.confirm ?? 'Confirm' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.choice-set .btn.selected {
  background-color: var(--bs-primary);
  color: var(--bs-white);
}
</style>
